import { useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import { Analytics } from "@models/analytics.model";
import { AnalyticsService } from "@services/analytics/AnalyticsService";
import { appStore } from "@store/appStore";
import { clientDashboardEvents, TreeNode } from "./clientDashboardEvents";
import { findCampaign } from "./campaignDates";
import DashHeader from "./components/DashHeader";
import CampaignDates from "./components/CampaignDates";
import UpcomingCampaignNotice from "./components/UpcomingCampaignNotice";
import NoCampaignsNotice from "./components/NoCampaignsNotice";
import PerformanceLaunchInsights from "./components/PerformanceLaunchInsights";
import ConversionsByTargetAction from "./components/ConversionsByTargetAction";
import StudentsConversion from "./components/StudentsConversion";
import ListSize from "./components/ListSize";
import AdsView from "./components/AdsView";
import TopNetwork from "./components/TopNetwork";
import TodosTile from "./components/TodosTile";
import DedicatedEngagementTeam from "./components/DedicatedEngagementTeam";
import FileUploadSummary from "./components/FileUploadSummary";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import DashboardBanner from "./banner/DashboardBanner";
import "./dashboard.scss";

export default function ClientDashboard() {
    // "Partner Dashboard" since MPN-1507. This is the new platform's client dashboard; the
    // old platform's is OldClientDashboard, now "Legacy Dashboard".
    useDocumentTitle("Partner Dashboard");
    const [analytics, setAnalytics] = useState<Analytics>(new Analytics());
    const snap = useSnapshot(clientDashboardEvents);

    useEffect(() => {
        clientDashboardEvents.initFromUrl();
    }, []);

    useEffect(() => {
        if (clientDashboardEvents.orgIds.length === 0) {
            setAnalytics(new Analytics());
            return;
        }

        const load = async () => {
            try {
                const service = new AnalyticsService();
                const result = await service.getAnalytics({
                    organizationIds: [...clientDashboardEvents.orgIds],
                    days: clientDashboardEvents.days,
                    status: clientDashboardEvents.status,
                    engagement: clientDashboardEvents.engagement,
                    objective: clientDashboardEvents.objective,
                    launch: clientDashboardEvents.launch,
                    employeeId: appStore.loggedInUser?.employee?.id ?? null,
                });
                setAnalytics(result);

                if (result.charts?.["filter-tree"]) {
                    clientDashboardEvents.setFilterTree(result.charts["filter-tree"]);
                }
            } catch (err) {
                console.error("Error loading analytics:", err);
            }
        };

        load();
    }, [snap.filterChangedTrigger]);

    // A single Upcoming campaign shows when it starts instead of a screen of zeroes (AC3).
    const upcomingOnly =
        findCampaign(snap.filterTree as TreeNode[], snap.launch)?.status === "UPCOMING";

    // A status that matched no campaign gets one empty state rather than a dashboard of zeroes
    // (PO review). The count comes from the server, which is what scoped every chart — counting
    // the filter tree instead would call an organization empty whose only ended campaign hangs
    // off no objective, over charts showing that campaign's real numbers. null is All, or a
    // response that has not arrived yet.
    const unmatchedStatus =
        snap.status !== "ALL" && analytics.statusMatches === 0 ? snap.status : null;

    return (
        <>
            <DashboardBanner />
            <div className="client-dashboard">
                <div className="client-dashboard__welcome">
                    <div className="client-dashboard__welcome-title">
                        Welcome to your Partner Dashboard
                    </div>
                    <div className="client-dashboard__welcome-body">
                        Once your campaign begins, access this page to view your campaign performance on-demand.
                    </div>
                </div>
                <DashHeader />
                <CampaignDates />
                {unmatchedStatus ? (
                    <NoCampaignsNotice status={unmatchedStatus} />
                ) : upcomingOnly ? (
                    <UpcomingCampaignNotice />
                ) : (
                    <>
                        <PerformanceLaunchInsights analytics={analytics} />
                        <ConversionsByTargetAction analytics={analytics} />
                        <div className="client-dashboard__row">
                            <StudentsConversion analytics={analytics} />
                            <ListSize analytics={analytics} />
                        </div>
                        <AdsView analytics={analytics} />
                    </>
                )}
                <div className="client-dashboard__row">
                    <TodosTile analytics={analytics} />
                    <TopNetwork analytics={analytics} />
                </div>
                <div className="client-dashboard__row">
                    <DedicatedEngagementTeam analytics={analytics} />
                    <FileUploadSummary analytics={analytics} />
                </div>
            </div>
        </>
    );
}
